import React from 'react';
import {
  Card, CircularGaugeChart, ellipsis, FontWeight, Helper, HStack, Icon, ICON_ALERT_CIRCLE_OUTLINE, ICON_INFORMATION_OUTLINE, Message, Spacing, Stack,
  Text,
} from '@doveit/bricks';
import Markdown from 'react-markdown';
import { ConversionMetric } from '../../../providers/api/dtos';
import { configByCr, configByCrPerformanceRating } from './constants';
import { template } from '../../../utils/text/text';

export interface AgentConversionRateSummaryCardProps extends React.AriaAttributes {
  cr: 'cr2' | 'cr3',
  metric: ConversionMetric,
}

const AgentConversionRateSummaryCard: React.FC<AgentConversionRateSummaryCardProps> = ({
  cr,
  metric,
  ...rest
}) => {
  const cfgByCr = configByCr[cr];
  const cfgByCrPerformanceRating = metric.status ? configByCrPerformanceRating[metric.status] : configByCrPerformanceRating.NOT_DEFINED;

  const goToSerpPage = () => window.open(cfgByCr.serpLink, '_blank');

  return (
    <Card {...rest}>
      <Card.Content>
        <Spacing padding={[50, 0]}>
          <HStack
            gap={100}
            alignItems="start"
            justifyContent="space-between"
            wrap="nowrap"
          >
            <HStack
              gap={300}
              alignItems="start"
              wrap="nowrap"
            >
              <div style={{ width: '6.25rem' }}>
                <Stack
                  gap={150}
                  alignItems="center"
                >
                  <CircularGaugeChart
                    progress={metric.progress ?? 0}
                    color={cfgByCrPerformanceRating.variant}
                    size="L"
                  />

                  <Text.Caption
                    color={`${cfgByCrPerformanceRating.variant}.default.low`}
                    css={ellipsis()}
                  >
                    {cfgByCr.chartCaption}
                  </Text.Caption>
                </Stack>
              </div>

              <Spacing padding={[75, 0]}>
                <Stack gap={150}>
                  {cfgByCrPerformanceRating.title && (
                    <Text.H4 fontWeight={FontWeight.LIGHT}>
                      <Markdown components={{ p: ({ _node, ...props }) => props.children }}>
                        {template(cfgByCrPerformanceRating.title, {
                          CR: cr.toUpperCase(),
                          CR_PERFORMANCE: metric.performance !== undefined ? (metric.performance * 100).toFixed() : 0,
                        })}
                      </Markdown>
                    </Text.H4>
                  )}

                  <Message
                    message={metric.status ? cfgByCr.message.crDefined : cfgByCr.message.crUndefined}
                    icon={ICON_ALERT_CIRCLE_OUTLINE}
                    type={cfgByCrPerformanceRating?.variant ?? 'neutral'}
                    onClick={metric.status ? goToSerpPage : undefined}
                  />
                </Stack>
              </Spacing>
            </HStack>

            <Helper
              content={template(cfgByCr.helperContent, {
                AVG_CR: metric.goal * 100,
              })}
            >
              <Icon
                path={ICON_INFORMATION_OUTLINE}
                size={24}
              />
            </Helper>
          </HStack>
        </Spacing>
      </Card.Content>
    </Card>
  );
};

export default AgentConversionRateSummaryCard;
